import React, { Component } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import Context from "shared/util/Context";

import "./UserThumb.scss";

const PLACEHOLDER_IMAGE = `${Context.get("cssImagePath")}media/img/user/placeholder_2013/pq_400.png`;

// a singleton will handle all the scroll stuff
// also, only one thumb per username!!!!!!!!
const UserThumbIndicatorRevealer = {
	_data: {
		initted: false,
		isFinding: false,
		userMap: {},
	},

	init() {
		if (this._data.initted) {
			return;
		}

		window.addEventListener("scroll", this.checkElementsSoon.bind(this));

		this.checkElements();
	},

	addElement(username, el) {
		this._data.userMap[username] = el;
	},

	checkElementsSoon() {
		if (!this._data.isFinding) {
			this._data.isFinding = true;

			requestAnimationFrame(() => {
				this._data.isFinding = false;
				this.checkElements();
			});
		}
	},

	checkElements() {
		const windowTop = window.scrollY;
		const windowBottom = windowTop + window.innerHeight;

		let reachedScreen = false;
		let backOffScreen = false;

		Object.values(this._data.userMap).forEach((bundle) => {
			if (backOffScreen) {
				return;
			}

			const { component, el } = bundle;

			if (component.state.isVisible) {
				return;
			}

			if (!el) {
				return;
			}

			const rect = el.getBoundingClientRect();
			const top = rect.top + windowTop;
			const bottom = top + rect.height;

			const allOnScreen = windowTop < top && bottom < windowBottom;

			if (allOnScreen) {
				component.setState({ isVisible: true });
				reachedScreen = true;
			} else if (reachedScreen) {
				backOffScreen = true;
			}
		});
	},
};

class UserThumb extends Component {
	static displayTypes = {
		Round: "round",
		Box: "box",
	};
	static indicatorTypes = {
		Boost: "likedViaBoost",
		Message: "messagedyou",
		Like: "likedyou",
	};
	static iconTypes = {
		Message: "messages",
		Like: "star",
	};

	static propTypes = {
		last_contacts: PropTypes.object,
		likes: PropTypes.object,
		thumbs: PropTypes.array,
		size: PropTypes.string,
		username: PropTypes.string.isRequired,
		isAlice: PropTypes.bool,
		indicatorWidth: PropTypes.oneOf([ 1, 2, 3, 4, 5, 6, 7, 8, 9, 10 ]),
		shouldDisableAnimationMessages: PropTypes.bool,
		shouldDisableAnimationLikes: PropTypes.bool,
		shape: PropTypes.oneOf(Object.values(UserThumb.displayTypes)),
	};
	static defaultProps = {
		shape: UserThumb.displayTypes.Round,
	};

	state = {
		isVisible: false,
		loadError: false,
	};

	componentDidMount() {
		UserThumbIndicatorRevealer.init();
	}

	onLoadError = () => {
		this.setState({
			loadError: true,
		});
	};

	getThumbnailImage = () => {
		const {
			thumbs,
			size = "225x225",
		} = this.props;
		const {
			loadError,
		} = this.state;

		// Something failed while loading, fallback!
		if (loadError) {
			return PLACEHOLDER_IMAGE;
		}

		// Try to get the user thumbnail.
		const userThumb = thumbs && thumbs[0]
			? thumbs[0][size]
			: null;

		// Return thumbnail or fallback to placeholder.
		return userThumb || PLACEHOLDER_IMAGE;
	};

	getIndicatorType = (icon) => {
		const {
			likes,
		} = this.props;

		// Boost color should override icons' default color.
		const boost = likes && likes.via_spotlight;

		if (boost) {
			return UserThumb.indicatorTypes.Boost;
		} else if (icon === UserThumb.iconTypes.Message) {
			return UserThumb.indicatorTypes.Message;
		} else if (icon === UserThumb.iconTypes.Like) {
			return UserThumb.indicatorTypes.Like;
		}

		return null;
	};

	getIconType = () => {
		const {
			isAlice,
			last_contacts: lastContacts,
			likes,
		} = this.props;

		// Reasons to show icons:
		const message = isAlice || lastContacts && lastContacts.reverse;
		const like = likes && (likes.they_like || likes.mutual_like);

		// Prioritize the icons: message, like.
		if (message) {
			return UserThumb.iconTypes.Message;
		} else if (like) {
			return UserThumb.iconTypes.Like;
		}

		return null;
	};

	shouldAnimate = (icon) => {
		const {
			shouldDisableAnimationMessages,
			shouldDisableAnimationLikes,
		} = this.props;

		// Custom logic to allow for disabling indicator animations.
		if (icon === UserThumb.iconTypes.Message) {
			return !shouldDisableAnimationMessages;
		} else if (icon === UserThumb.iconTypes.Like) {
			return !shouldDisableAnimationLikes;
		}

		return false;
	};

	render() {
		const {
			username,
			indicatorWidth,
			shape,
		} = this.props;

		// Get the thumbnail image for this user.
		const thumb = this.getThumbnailImage();

		// Type of icon to animate over image.
		const icon = this.getIconType();

		// Should we show an animation for this icon?
		const shouldAnimate = this.shouldAnimate(icon);

		// Type of indicator around image.
		const indicator = this.getIndicatorType(icon);

		const containerClass = classnames("userthumb", {
			[`userthumb--${process.env.PLATFORM}`]: true,
			[`userthumb--${shape}`]: true,
			[`userthumb--indicator-${indicatorWidth}px`]: !!indicator,
		});

		const thumbClass = classnames("userthumb-indicator", {
			"userthumb-indicator--show": this.state.isVisible && shouldAnimate,
			[`userthumb-indicator--${indicator}`]: true,
		});

		return (
			<span
				className={containerClass}
				ref={(el) => {
					UserThumbIndicatorRevealer._data.userMap[username] = {
						el,
						component: this,
					};
				}}>
				<img className="userthumb-img" src={thumb} alt={username} onError={this.onLoadError} />
				{indicator && (
					<span className={thumbClass}>
						{shouldAnimate && (
							<React.Fragment>
								<span className="userthumb-indicator-bg" />
								<span className={`userthumb-indicator-icon okicon i-${icon}`} />
							</React.Fragment>
						)}
					</span>
				)}
			</span>
		);
	}
}

export default UserThumb;



// WEBPACK FOOTER //
// ./src/shared/components/UserThumb.jsx